import * as React from 'react';

import { computeInitials } from '@klareospace/pharos-lib-utils';
import type { LucideIcon } from 'lucide-react';

import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EntityIconProps {
  /** Accessible label for the image and a default source for initials. */
  alt: string;
  /** CSS class name for size/color/shape overrides. */
  className?: string;
  /**
   * Icon rendered as the fallback when no `src` is provided.
   * Takes precedence over `fallbackText` when both are supplied.
   */
  fallbackIcon?: LucideIcon;
  /**
   * Explicit short text (e.g. initials or a code) rendered as the fallback
   * when no `src` and no `fallbackIcon` are provided. When omitted, initials
   * are computed from `alt`.
   */
  fallbackText?: string;
  /** Corner style for the text/icon fallback. Defaults to a square (rounded-md). */
  shape?: 'circle' | 'square';
  /** Variant controlling the icon size. */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Image/logo URL. When provided, the image is rendered. */
  src?: string;
  /** Color scheme for the text/icon fallback. Defaults to a neutral muted tone. */
  tone?: 'muted' | 'primary';
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SIZE_CLASSES: Record<NonNullable<EntityIconProps['size']>, string> = {
  lg: 'h-14 w-14 text-base',
  md: 'h-10 w-10 text-sm',
  sm: 'h-8 w-8 text-xs',
  xl: 'h-20 w-20 text-lg',
  // Smaller font-size than other variants so two-letter initials (e.g. "NC") fit
  // inside the 16px box without overflowing — see `overflow-hidden` on `base` below.
  xs: 'h-4 w-4 text-[8px]',
};

const ICON_SIZE_CLASSES: Record<NonNullable<EntityIconProps['size']>, string> = {
  lg: 'h-7 w-7',
  md: 'h-5 w-5',
  sm: 'h-4 w-4',
  xl: 'h-10 w-10',
  xs: 'h-3 w-3',
};

interface ToneStyle {
  bg: string;
  /** Legacy weight pairing carried by `tone` — bold for muted, medium for primary. Only applied to the text fallback (has no visible effect on the icon fallback, which has no text). */
  fontWeight: string;
  text: string;
}

const TONE_STYLES: Record<NonNullable<EntityIconProps['tone']>, ToneStyle> = {
  muted: { bg: 'bg-muted', fontWeight: 'font-bold', text: 'text-muted-foreground' },
  primary: { bg: 'bg-primary/10', fontWeight: 'font-medium', text: 'text-primary' },
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Generic, domain-agnostic icon/avatar. Renders, in priority order:
 * 1. an image when `src` is provided,
 * 2. a `fallbackIcon` when provided,
 * 3. text (`fallbackText`, or initials computed from `alt`).
 */
export function EntityIcon({
  alt,
  className,
  fallbackIcon: FallbackIcon,
  fallbackText,
  shape = 'square',
  size = 'sm',
  src,
  tone = 'muted',
}: EntityIconProps): React.JSX.Element {
  const base = cn(
    'flex shrink-0 items-center justify-center overflow-hidden',
    shape === 'circle' ? 'rounded-full' : 'rounded-md',
    SIZE_CLASSES[size],
    // Applied after the size classes: tailwind-merge treats `leading-*` as conflicting
    // with `text-[..]` (font-size implies a default line-height), so it must come last
    // to win and keep initials vertically tight instead of overflowing the box.
    'leading-none',
    className,
  );

  if (src) {
    return <img alt={alt} className={cn('object-contain', base)} src={src} />;
  }

  const toneStyle = TONE_STYLES[tone];

  if (FallbackIcon) {
    return (
      <div className={cn(toneStyle.bg, toneStyle.text, base)}>
        <FallbackIcon className={ICON_SIZE_CLASSES[size]} />
      </div>
    );
  }

  const text = fallbackText ?? computeInitials(alt);

  return <div className={cn(toneStyle.bg, toneStyle.fontWeight, toneStyle.text, base)}>{text}</div>;
}
